import { ReactNode, useEffect } from "react";
import { extractBrandSlug } from "@/lib/branding";
import { useGeneralFormStore } from "@/stores/generalFormStore";

// WHY: SPA must not trust the wire — re-validate brandSlug before interpolating into
// a redirect URL, mirroring BrandingProvider's hasRedirectFields guard. The DB CHECK
// constraint prevents arbitrary values today, but a future DB-bypass path that wrote
// brandSlug="evil.com?" would interpolate to https://evil.com?.gcheckin.com/... (open redirect).
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

/**
 * Wraps the form-route subtree (/f/:formId and its nested guest/vehicle/pet routes).
 *
 * Reconciles the SPA hostname with the form-data's brandSlug in both directions:
 *
 * FORWARD (canonical/legacy → branded): when the form-app loads on the canonical or
 * legacy default domain AND the form's listing qualifies for brand application
 * (Business-tier per-listing — see backend `PublicApiImpl#_getFormData`), redirects
 * to the branded subdomain preserving path + search.
 *
 * REVERSE (branded → canonical): when the form-app loads on a branded subdomain BUT
 * the form-data either omits brandSlug (per-listing brand gate suppressed it because
 * the form's listing is NOT on Business tier — even though the Owner may have a
 * configured brand) OR carries a brandSlug that does NOT match the current
 * subdomain (stale cross-Owner link), redirects to the canonical domain preserving
 * path + search so the form renders with default GCI branding.
 *
 * The reverse direction exists because brand application is gated PER-LISTING (the
 * per-listing tier-application correction): an Owner on Business may have other
 * listings on Basic/Premium whose forms must NOT render under that Owner's brand.
 *
 * On branded subdomains where brandSlug matches, this component is a passthrough —
 * BrandingProvider handles slug-keyed branding via the public branding endpoint.
 *
 * # WHY subscribe-not-fetch: the form-app already fetches PublicFormDataApiDto via
 * useGeneralFormStore.initializeForm. Duplicating the fetch here would double cold-start
 * latency on every form load. Subscribing to the brandSlug slice keeps the fetch path
 * single-source.
 */
export function FormDataBrandRedirect({ children }: { children: ReactNode }) {
  const brandSlug = useGeneralFormStore((state) => state.brandSlug);

  // WHY case-split: brand application is now gated PER-LISTING in the backend, so a
  // branded subdomain can legitimately receive a form whose brandSlug is null (Basic
  // listing of a Business-brand Owner) or whose brandSlug points to a different
  // Owner (stale cross-Owner link). Both surfaces require the REVERSE redirect to
  // the canonical domain, where the form serves with default GCI branding. The
  // FORWARD direction (canonical → branded) only fires on canonical/legacy hosts.
  useEffect(() => {
    const currentSlug = extractBrandSlug(window.location.hostname);

    if (currentSlug) {
      if (!brandSlug || brandSlug !== currentSlug) {
        const canonical = `https://gcheckin.com${window.location.pathname}${window.location.search}`;
        window.location.replace(canonical);
      }
      return;
    }

    if (!brandSlug) return;
    if (!SLUG_PATTERN.test(brandSlug)) return;
    const branded = `https://${brandSlug}.gcheckin.com${window.location.pathname}${window.location.search}`;
    window.location.replace(branded);
  }, [brandSlug]);

  return <>{children}</>;
}
